import {BLEND_MODES, Container, Point, Sprite, Text, TextMetrics} from 'pixi.js';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {CursorContext} from '@gongxigames/framework/core/fragments/display/richText/CursorContext';
import {Node, NodeTag} from 'posthtml-parser';
import {RichTextTextStyle, TextTransform} from '@gongxigames/framework/core/fragments/display/richText/TextStyle';

type PropParsers = Record<string, Function>;

const Parsers = {
    parseStringArray(str: string): string[] {
        return str.split(',');
    },

    parseNumberArray(str: string): number[] {
        return str.split(',').map(str => Parsers.parseNumber(str));
    },

    parsePoint(str: string): Point {
        const data = Parsers.parseNumberArray(str);

        return new Point(data[0], data[1] ?? data[0]);
    },

    parseNumber(str: string): number {
        const num = Number(str);

        if (Number.isNaN(num)) {
            throw new Error(`can't pare number: ${str}`);
        }

        return num;
    }
} as const;

function applyPropsParsers(parsers: PropParsers, attrs: Record<string, string>, obj: any, _tag: string): void {
    Object
        .entries(attrs)
        .forEach(([key, val]) => {
            if (parsers[key]) {
                obj[key] = parsers[key](val);
            }
        });
}

/**
 * Tag base Class. Tags classes are used for applying xml-tag related actions.
 */
export abstract class Tag {
    public abstract test(node: Node): boolean;
    public abstract run(context: CursorContext, node: Node, blendMode: BLEND_MODES): void;
}

/**
 * A simple new line action.
 */
export class TagBr extends Tag {
    public test(node: NodeTag): boolean {
        return node.tag === 'br';
    }

    public run(context: CursorContext, node: NodeTag): void {
        const style = context.getCurrentLevelStyle();
        const metrics = TextMetrics.measureText('Some Text', style);

        context.newLine(metrics.lineHeight, style);
    }
}

/**
 * A simple 'add image' action.
 */
export class TagImage extends Tag {
    static tags = ['image', 'img'];

    static props: PropParsers = {
        scale: Parsers.parsePoint,
        anchor: Parsers.parsePoint,
        rotation: Parsers.parseNumber,
        width: Parsers.parseNumber,
        height: Parsers.parseNumber,
        name: String,
        alpha: Parsers.parseNumber
    };

    public test(node: NodeTag): boolean {
        return TagImage.tags.includes(node.tag as string);
    }

    public run(context: CursorContext, node: NodeTag, blendMode: BLEND_MODES): void {
        const {resourceLoader} = context;
        const {attrs, tag} = node;

        if (!resourceLoader) {
            throw new Error(`ResourceLoader is required for ${tag} tag`);
        }

        if (!attrs) {
            throw new Error(`Tag ${tag} don't have attributes`);
        }

        const sizeOffset = attrs.sizeOffset ? Parsers.parsePoint(attrs.sizeOffset) : new Point();
        const {src, position} = attrs;

        if (!src) {
            consoleLogger.error(new Error(`wrong ${tag} tag, missing src`));

            return;
        }

        const img = new Sprite(resourceLoader.getTexture(src));

        img.position.copyFrom(context.getCursor());
        img.blendMode = blendMode;

        applyPropsParsers(TagImage.props, attrs, img, node.tag as string);

        if (position) {
            context.getCurrentLevelContainer().position = Parsers.parsePoint(position);
        }

        context.addToLine(img, img.width + sizeOffset.x, img.height + sizeOffset.y, 0);
    }
}

/**
 * Add text action.
 */
export class TagTextElement extends Tag {

    public test(node: NodeTag): boolean {
        return typeof node === 'string';
    }

    public run(context: CursorContext, node: string, blendMode: BLEND_MODES): void {
        const style = context.getCurrentLevelStyle();
        const transformedText = this.applyTransform(node, style.textTransform);
        const text = new Text(transformedText, new RichTextTextStyle(style));
        const {lines, lineWidths, lineHeight, fontProperties} = TextMetrics.measureText(transformedText, style);
        const width = style.wordWrap ? Math.max(...lineWidths) : lineWidths[0];

        text.resolution = context.resolution;
        text.position.copyFrom(context.getCursor());
        text.blendMode = blendMode;

        context.addToLine(text, width, lineHeight, fontProperties.descent, true);

        // add empty lines when word wrap enabled
        for (let i = 1; i < lines.length; i++) {
            context.newLine(lineHeight, style);
            context.addToLine(new Container(), lineWidths[i], lineHeight, fontProperties.descent, true);
        }
    }

    private applyTransform(node: string, transform: TextTransform): string {
        if (transform === 'uppercase') {
            return node.toUpperCase();
        } else if (transform === 'lowercase') {
            return node.toLowerCase();
        }

        return node;
    }
}

/**
 * Text Style formatting action.
 */
export class TagStyle extends Tag {
    static props: PropParsers = {
        align: String,
        verticalAlign: String,
        textTransform: String,
        breakWords: Boolean,
        dropShadow: Boolean,
        dropShadowAlpha: Parsers.parseNumber,
        dropShadowAngle: Parsers.parseNumber,
        dropShadowBlur: Parsers.parseNumber,
        dropShadowColor: String,
        dropShadowDistance: Parsers.parseNumber,
        fill: Parsers.parseStringArray,
        fillGradientType: Parsers.parseNumber,
        fillGradientStops: Parsers.parseNumberArray,
        fontFamily: Parsers.parseStringArray,
        fontSize: Parsers.parseNumber,
        fontStyle: String,
        fontVariant: String,
        fontWeight: String,
        letterSpacing: Parsers.parseNumber,
        lineHeight: Parsers.parseNumber,
        lineJoin: String,
        miterLimit: Parsers.parseNumber,
        padding: Parsers.parseNumber,
        stroke: String,
        strokeThickness: Parsers.parseNumber,
        textBaseline: String,
        trim: Boolean,
        whiteSpace: String,
        wordWrap: Boolean,
        wordWrapWidth: Parsers.parseNumber,
        leading: Parsers.parseNumber
    };

    public test(node: NodeTag): boolean {
        return node.tag === 'style';
    }

    public run(context: CursorContext, node: NodeTag): void {
        if (!node.attrs) {
            return;
        }

        const {id, position, relative, scale, rotation, alpha, name} = node.attrs;

        if (id) {
            context.setCurrentStyleByName(id);
        }

        const style = context.getCurrentLevelStyle();

        applyPropsParsers(TagStyle.props, node.attrs, style, node.tag as string);

        if (position) {
            let posCursor = context.getCursor().clone();
            const pos = Parsers.parsePoint(position);

            if (relative || relative === '') {
                posCursor = pos;
            } else {
                posCursor.set(-posCursor.x + pos.x, -posCursor.y + pos.y);
            }

            context.setCurrentLevelCursorOffset(posCursor);
        }

        if (scale) {
            context.getCurrentLevelContainer().scale = Parsers.parsePoint(scale);
        }

        if (rotation) {
            const posCursorRotation = context.getCursor().clone();
            const container = new Container();
            const currentContainer = context.getCurrentLevelContainer();

            currentContainer.position.copyFrom(posCursorRotation);

            posCursorRotation.set(-posCursorRotation.x, -posCursorRotation.y);
            context.setCurrentLevelCursorOffset(posCursorRotation);

            currentContainer.addChild(container);
            container.rotation = (Math.PI / 180) * Number(rotation);

            context.setCurrentLevelContainer(container);
        }

        if (node.attrs.hasOwnProperty('alpha')) {
            context.getCurrentLevelContainer().alpha = Number(alpha);
        }

        if (node.attrs.hasOwnProperty('mask')) {
            context.setCurrentLevelIsMask(true);
        }

        if (name) {
            context.getCurrentLevelContainer().name = node.attrs.name;
        }
    }
}

export const Tags: Tag[] = [
    new TagBr(),
    new TagImage(),
    new TagStyle(),
    new TagTextElement()
];
